import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { BoletaService, BoletaVenta, BoletaResumen, EmisorConfig } from '../../../core/services/boleta.service';
import { PeruDatePipe } from '../../../shared/pipes/peru-date.pipe';

@Component({
  selector: 'app-boletas-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    DialogModule,
    TagModule,
    TooltipModule,
    ToastModule,
    PeruDatePipe
  ],
  providers: [MessageService],
  templateUrl: './boletas-admin.html',
  styleUrls: ['./boletas-admin.css']
})
export class BoletasAdminComponent implements OnInit {
  boletas: BoletaVenta[] = [];
  resumen: BoletaResumen | null = null;
  loading = true;
  first = 0;
  rows = 10;
  totalRecords = 0;
  private requestSeq = 0;

  selectedYear = new Date().getFullYear();
  selectedMonth = new Date().getMonth() + 1;
  filtroOrigen: '' | 'MANUAL' | 'WEB' = '';

  configDialog = false;
  emailDialog = false;
  nrusDialog = false;
  emailSubmitted = false;
  emailInput = '';
  boletaParaEnviar: BoletaVenta | null = null;
  boletaNrus: BoletaVenta | null = null;
  loadingNrus = false;
  sendingEmail = false;
  savingConfig = false;

  emisor: EmisorConfig = {
    ruc: '',
    razonSocial: '',
    titular: '',
    eslogan: 'ROPA URBANA | LIMA, PERÚ',
    direccion: '',
    instagram: '@moananioficial',
    website: 'moanani.com',
    serie: 'B001',
    autoEmitirAlPagar: false,
    igvPorcentaje: 18
  };

  months = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];

  years: number[] = [];

  constructor(
    private boletaService: BoletaService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
    const current = new Date().getFullYear();
    for (let y = current; y >= current - 5; y--) {
      this.years.push(y);
    }
  }

  ngOnInit(): void {
    this.loadEmisor();
    this.loadResumen();
  }

  origenLabel(origen?: string | null): string {
    if (origen === 'MANUAL') return 'Manual';
    if (origen === 'WEB') return 'Web';
    return '—';
  }

  loadEmisor(): void {
    this.boletaService.getEmisorConfig().subscribe({
      next: (cfg) => {
        this.emisor = { ...cfg };
        this.cdr.detectChanges();
      },
      error: () => this.messageService.add({
        severity: 'warn',
        summary: 'Configuración',
        detail: 'No se pudo cargar la configuración del emisor'
      })
    });
  }

  onPeriodChange(): void {
    this.loadResumen();
    this.reloadFirst();
  }

  onOrigenChange(): void {
    this.loadResumen();
    this.reloadFirst();
  }

  reloadFirst(): void {
    this.first = 0;
    this.loadBoletas({ first: 0, rows: this.rows });
  }

  refresh(): void {
    this.loadResumen();
    this.loadBoletas({ first: this.first, rows: this.rows });
  }

  loadBoletas(event?: TableLazyLoadEvent): void {
    const seq = ++this.requestSeq;
    this.loading = true;
    const first = event?.first ?? this.first;
    const size = event?.rows || this.rows;
    this.first = first || 0;
    this.rows = size;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;

    this.boletaService.listar(
      this.selectedYear,
      this.selectedMonth,
      page,
      this.rows,
      this.filtroOrigen || undefined
    ).subscribe({
      next: (data) => {
        if (seq !== this.requestSeq) return;
        this.boletas = data?.content || [];
        this.totalRecords = data?.totalElements || 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (seq !== this.requestSeq) return;
        this.loading = false;
        this.boletas = [];
        this.totalRecords = 0;
        this.cdr.detectChanges();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'No se pudieron cargar las boletas'
        });
      }
    });
  }

  loadResumen(): void {
    this.boletaService.resumen(this.selectedYear, this.selectedMonth, this.filtroOrigen || undefined).subscribe({
      next: (r) => {
        this.resumen = r;
        this.cdr.detectChanges();
      },
      error: () => {
        this.resumen = null;
        this.cdr.detectChanges();
      }
    });
  }

  openConfig(): void {
    this.loadEmisor();
    this.configDialog = true;
  }

  guardarEmisor(): void {
    if (!this.emisor.ruc?.trim() || !this.emisor.razonSocial?.trim() || !this.emisor.serie?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'RUC, razón social y serie son obligatorios'
      });
      return;
    }
    const igv = Number(this.emisor.igvPorcentaje);
    if (Number.isNaN(igv) || igv < 0 || igv > 100) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El IGV debe ser un porcentaje entre 0 y 100'
      });
      return;
    }
    this.emisor.igvPorcentaje = igv;

    this.savingConfig = true;
    this.boletaService.updateEmisorConfig(this.emisor).subscribe({
      next: (cfg) => {
        this.emisor = { ...cfg };
        this.savingConfig = false;
        this.configDialog = false;
        this.cdr.detectChanges();
        this.messageService.add({
          severity: 'success',
          summary: 'Guardado',
          detail: 'Configuración del emisor actualizada'
        });
      },
      error: (err) => {
        this.savingConfig = false;
        this.cdr.detectChanges();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'No se guardó la configuración'
        });
      }
    });
  }

  descargarPdf(boleta: BoletaVenta): void {
    this.boletaService.descargarPdf(boleta.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${boleta.numeroCompleto}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.messageService.add({
        severity: 'error',
        summary: 'PDF',
        detail: 'No se pudo descargar el PDF'
      })
    });
  }

  onEnviarClick(boleta: BoletaVenta): void {
    if (boleta.envioCliente === 'ENVIADO') {
      this.messageService.add({
        severity: 'info',
        summary: 'Envío',
        detail: `Ya fue enviada a ${boleta.clienteEmail || 'el cliente'}`
      });
      return;
    }

    if (boleta.clienteEmail?.trim()) {
      this.enviarConEmail(boleta, boleta.clienteEmail.trim());
      return;
    }

    this.boletaParaEnviar = boleta;
    this.emailInput = '';
    this.emailSubmitted = false;
    this.emailDialog = true;
  }

  confirmarEnvioConEmail(): void {
    this.emailSubmitted = true;
    const email = this.emailInput?.trim();
    if (!email || !this.isValidEmail(email) || !this.boletaParaEnviar) {
      return;
    }
    this.enviarConEmail(this.boletaParaEnviar, email);
  }

  private enviarConEmail(boleta: BoletaVenta, email?: string): void {
    this.sendingEmail = true;
    this.cdr.detectChanges();

    this.boletaService.enviarAlCliente(boleta.id, email).subscribe({
      next: (updated) => {
        const idx = this.boletas.findIndex(b => b.id === updated.id);
        if (idx >= 0) this.boletas[idx] = updated;
        if (this.resumen && updated.envioCliente === 'ENVIADO') {
          this.resumen = {
            ...this.resumen,
            cantidadSinEnviar: Math.max(0, (this.resumen.cantidadSinEnviar ?? 0) - 1)
          };
        }
        this.sendingEmail = false;
        this.emailDialog = false;
        this.boletaParaEnviar = null;
        this.cdr.detectChanges();
        this.messageService.add({
          severity: 'success',
          summary: 'Enviado',
          detail: `Boleta ${updated.numeroCompleto} enviada a ${updated.clienteEmail}`
        });
      },
      error: (err) => {
        this.sendingEmail = false;
        this.cdr.detectChanges();
        const msg = err?.error?.message || 'No se pudo enviar la boleta';
        if (msg.toLowerCase().includes('correo') && !this.emailDialog) {
          this.boletaParaEnviar = boleta;
          this.emailInput = '';
          this.emailSubmitted = false;
          this.emailDialog = true;
        }
        this.messageService.add({ severity: 'error', summary: 'Envío', detail: msg });
      }
    });
  }

  isValidEmail(email: string): boolean {
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);
  }

  formatCorrelativo(n: number | null | undefined): string {
    const num = Number(n) || 0;
    return String(Math.max(0, num)).padStart(6, '0');
  }

  envioSeverity(estado: string): 'success' | 'warn' {
    return estado === 'ENVIADO' ? 'success' : 'warn';
  }

  envioLabel(estado: string): string {
    return estado === 'ENVIADO' ? 'Enviado' : 'Pendiente';
  }

  openNrus(boleta: BoletaVenta): void {
    this.nrusDialog = true;
    this.boletaNrus = boleta;
    this.loadingNrus = true;
    this.cdr.detectChanges();

    this.boletaService.obtenerPorId(boleta.id).subscribe({
      next: (full) => {
        this.boletaNrus = full;
        this.loadingNrus = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingNrus = false;
        this.cdr.detectChanges();
        this.messageService.add({
          severity: 'warn',
          summary: 'NRUS',
          detail: 'Se muestran los datos de la lista; no se pudo refrescar el detalle.'
        });
      }
    });
  }

  detalleProductoTexto(boleta: BoletaVenta | null): string {
    if (!boleta?.lineas?.length) return '';
    return boleta.lineas.map(l => {
      const desc = (l.descripcion || 'Producto')
        .split('\n')
        .map(s => s.trim())
        .filter(s => s && s.toUpperCase() !== 'MN')
        .join(' — ');
      const qty = l.cantidad ?? 1;
      return qty > 1 ? `${desc} (x${qty})` : desc;
    }).join('\n');
  }

  formatMoneyPlain(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '0.00';
    return Number(value).toFixed(2);
  }

  nrusCampos(): { label: string; value: string }[] {
    const b = this.boletaNrus;
    if (!b) return [];
    return [
      { label: 'DNI cliente', value: (b.clienteDni || '').trim() || '-' },
      { label: 'Nombre', value: (b.clienteNombre || '').trim() || '-' },
      { label: 'Dirección', value: (b.clienteDireccion || '').trim() || '-' },
      { label: 'Detalle Producto', value: this.detalleProductoTexto(b) || '-' },
      { label: 'Subtotal', value: this.formatMoneyPlain(b.subtotal) },
      { label: 'IGV', value: this.formatMoneyPlain(b.igv) },
      { label: 'Total', value: this.formatMoneyPlain(b.total) }
    ];
  }

  copyToClipboard(text: string, label?: string): void {
    const value = (text || '').trim();
    if (!value || value === '-') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Copiar',
        detail: 'No hay dato para copiar',
        life: 2000
      });
      return;
    }
    navigator.clipboard.writeText(value).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copiado',
        detail: label ? `${label} copiado` : 'Texto copiado',
        life: 1800
      });
    }).catch(() => {
      this.messageService.add({
        severity: 'warn',
        summary: 'Error',
        detail: 'No se pudo copiar',
        life: 2000
      });
    });
  }

  copiarTodoNrus(): void {
    const block = this.nrusCampos()
      .map(c => `${c.label}: ${c.value}`)
      .join('\n');
    this.copyToClipboard(block, 'Datos NRUS');
  }
}
